/**
 * Bound a time-remaining estimate by the pace the server has reported
 * (ZLAYT-1423).
 *
 * The local estimate (rawEstimatedTimeLeft in index.tsx) sums
 * getExpectedDurationSeconds per unfinished task: each task's share of
 * TOTAL_ESTIMATED_SECONDS by weight, with a 60 s floor, or 180 s for a task
 * type the frontend does not know. Tasks run concurrently, so the sum
 * overstates the remaining time and a two-minute generation showed "About
 * 13 minutes". Once the server has reported enough progress over enough
 * elapsed time, the remaining time implied by that pace caps the estimate.
 * It never lengthens one.
 *
 * Only a server-reported percentage (overallProgressFromEvent) is accepted;
 * the synthetic, timer-driven progress the hook also produces is not a
 * measurement and must not be extrapolated. A null percentage means "no
 * server figure yet" and leaves the estimate untouched.
 */

/**
 * Provisional thresholds. They were chosen by hand for the QA D-27 report
 * (generations of about two minutes) and have not been fitted to recorded
 * runs; revisit them against task timings from the backend's progress
 * events (document_generator/tasks/instance.py) once those are logged.
 */
export const CALIBRATION_MIN_PROGRESS_PCT = 5;
export const CALIBRATION_MIN_ELAPSED_SECONDS = 20;

export function calibrateRemainingSeconds(
  estimateSeconds: number,
  serverProgressPct: number | null,
  elapsedSeconds: number
): number {
  const est = Math.max(0, estimateSeconds || 0);
  if (serverProgressPct === null || !Number.isFinite(serverProgressPct)) {
    return Math.round(est);
  }
  if (serverProgressPct >= 100) return 0;
  if (
    serverProgressPct < CALIBRATION_MIN_PROGRESS_PCT ||
    elapsedSeconds < CALIBRATION_MIN_ELAPSED_SECONDS
  ) {
    return Math.round(est);
  }
  const observed =
    (elapsedSeconds * (100 - serverProgressPct)) / serverProgressPct;
  return Math.round(Math.min(est, observed));
}
